/**
 * Minimal focusable-element contract retained without coupling dialog lifecycle to one DOM type.
 *
 * It represents the exact user control that opened a map dialog and may become disconnected after
 * a map rerender before focus is restored.
 */
export interface DialogFocusTarget {
  /** Whether the retained opener is still attached to its document. */
  readonly isConnected: boolean;
  /** Moves document focus to the retained opener. */
  focus(): void;
}

/**
 * Captures the exact map opener and restores focus after every centralized dialog close path.
 */
export class DialogFocusRestorer {
  private target: DialogFocusTarget | null = null;

  /**
   * Replaces the retained opener for the next dialog lifecycle.
   *
   * @param target Exact polygon or other focusable element that initiated opening, or null when unavailable.
   * @returns Nothing.
   */
  public remember(target: DialogFocusTarget | null): void {
    this.target = target;
  }

  /**
   * Returns focus to the retained opener once and clears the reference.
   * Disconnected elements are deliberately ignored so a rerendered map cannot receive stale focus.
   *
   * @returns Nothing.
   */
  public restore(): void {
    if (this.target?.isConnected) {
      this.target.focus();
    }
    this.target = null;
  }
}
